import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { type WorkQueueCase } from '../../../../services/workQueue';
import { requestRevision } from '../../../../services/approval';
import { ClarificationHistory } from '../ClarificationHistory';
import { ConfirmModalShell } from './ConfirmModalShell';
import { ConfirmModalHeader } from './ConfirmModalHeader';
import { ConfirmModalBody } from './ConfirmModalBody';
import { ConfirmModalFooter } from './ConfirmModalFooter';
import { PatientSummaryCard } from './PatientSummaryCard';
import { ReasonField } from './ReasonField';
import { REASON_MAX_LENGTH, REASON_MIN_LENGTH } from './reasonLimits';
import { useReasonActionSubmit } from './useReasonActionSubmit';

interface RequestChangesModalProps {
    workCase: WorkQueueCase;
    onClose: () => void;
    onSuccess: (actionId: string) => void;
}

function RequestChangesConsequenceNotice() {
    return (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            This sends the case back to the ortho team as &quot;Submit for redesign&quot;, with your
            note as the revision request.
        </p>
    );
}

function RevisionHistory({ patientId }: { patientId: string }) {
    return (
        <div className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-gray-700">Revision history</span>
            <div className="flex flex-col max-h-60 rounded-lg border border-gray-200 overflow-hidden">
                <ClarificationHistory
                    patientId={patientId}
                    queryKeyPrefix="redesign-conversation"
                    config={{ revisionEventType: 'request-revision' }}
                    embedded
                />
            </div>
        </div>
    );
}

export function RequestChangesModal({ workCase: c, onClose, onSuccess }: RequestChangesModalProps) {
    const { reason, setReason, submitting, canSubmit, confirm } = useReasonActionSubmit(
        c,
        onSuccess,
        {
            run: requestRevision,
            successToast: `Changes requested: ${c.name}`,
            fallbackMessage: 'Failed to request changes. Please try again.',
            source: 'RequestChangesModal.handleConfirm',
            action: 'request-revision'
        }
    );

    return (
        <ConfirmModalShell widthClassName="w-[520px]" dismissible={!submitting} onDismiss={onClose}>
            <ConfirmModalHeader
                icon={ArrowPathIcon}
                iconClassName="w-5 h-5 text-amber-500"
                title="Request changes"
                onClose={onClose}
                disabled={submitting}
            />

            <ConfirmModalBody className="space-y-3">
                <PatientSummaryCard name={c.name} practice={c.practice} />
                <RequestChangesConsequenceNotice />
                <RevisionHistory patientId={c.id} />
                <ReasonField
                    value={reason}
                    onChange={setReason}
                    disabled={submitting}
                    label="Note for the ortho team"
                    placeholder="What should change in this design?"
                    minLength={REASON_MIN_LENGTH}
                    maxLength={REASON_MAX_LENGTH}
                    tone="amber"
                />
            </ConfirmModalBody>

            <ConfirmModalFooter
                onCancel={onClose}
                onConfirm={confirm}
                cancelDisabled={submitting}
                confirmDisabled={!canSubmit}
                confirmLabel={submitting ? 'Sending…' : 'Send request'}
                confirmColorClassName="bg-amber-600 text-white hover:bg-amber-700"
            />
        </ConfirmModalShell>
    );
}
